import type { AdminDashboardStats } from '../types/admin';
import {
  getStoredAuthToken,
  hasStoredOwnerAdminSession,
  shouldUseDevOwnerAuth,
} from '../utils/authStorage';

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'user' | 'superadmin';
   adminPermissions?: string[];
  accountStatus?: 'good' | 'past_due' | 'suspended';
  createdAt: Date;
  updatedAt: Date;
}

export interface AdminLoginResponse {
  token: string;
  user: AdminUser;
}

export interface UserManagementResponse {
  users: AdminUser[];
  total: number;
}

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5001';
const API_ROOT = `${API_BASE}/api/admin`;

const authHeaders = (): HeadersInit => {
  const token = getStoredAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (hasStoredOwnerAdminSession() || shouldUseDevOwnerAuth()) {
    headers['X-Tellurvision-Dev-Owner'] = 'true';
  }
  return headers;
};

export const fetchAdminUsers = async (): Promise<UserManagementResponse> => {
  const response = await fetch(`${API_ROOT}/users`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    let extra = '';
    try {
      const data = (await response.json()) as { message?: string };
      if (data && typeof data.message === 'string') {
        extra = `: ${data.message}`;
      } else if (response.status === 401 || response.status === 403) {
        extra = ': unauthorized – admin access required';
      }
    } catch {
      if (response.status === 401 || response.status === 403) {
        extra = ': unauthorized – admin access required';
      }
    }

    throw new Error(`Failed to fetch users${extra}`);
  }

  return response.json();
};

export const createAdminUser = async (payload: {
  username?: string;
  email: string;
  password: string;
}): Promise<AdminUser> => {
  const response = await fetch(`${API_ROOT}/users`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let extra = ` (status ${response.status})`;
    try {
      const data = (await response.json()) as { message?: string };
      if (data && typeof data.message === 'string') {
        extra += `: ${data.message}`;
      }
    } catch {
      // ignore JSON parse issues; keep status code
    }

    // Normalize a couple of common cases even if the backend message changes.
    if (response.status === 409) {
      extra += ' — a user with this email may already exist.';
    } else if (response.status === 400) {
      extra += ' — check that email is set and password is at least 6 characters.';
    } else if (response.status === 401 || response.status === 403) {
      extra += ' — admin authentication is required (try logging out and back in).';
    }

    throw new Error(`Failed to create user${extra}`);
  }

  return response.json();
};

export const adminLogin = async (
  username: string,
  password: string,
): Promise<AdminLoginResponse> => {
  const response = await fetch(`${API_ROOT}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) {
    throw new Error('Login failed');
  }
  return response.json();
};

export const updateUserRole = async (userId: string, role: 'admin' | 'user'): Promise<void> => {
  const response = await fetch(`${API_ROOT}/users/${userId}/role`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ role }),
  });
  if (!response.ok) {
    throw new Error('Failed to update user role');
  }
};

export const fetchAdminUser = async (userId: string): Promise<AdminUser> => {
  const response = await fetch(`${API_ROOT}/users/${userId}`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to load user');
  }

  return response.json();
};

export const updateAdminUser = async (
  userId: string,
  updates: Partial<Pick<AdminUser, 'username' | 'email'>>,
): Promise<AdminUser> => {
  const response = await fetch(`${API_ROOT}/users/${userId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error('Failed to update user');
  }

  return response.json();
};

export const resetAdminUserPassword = async (userId: string, password: string): Promise<void> => {
  const response = await fetch(`${API_ROOT}/users/${userId}/reset-password`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ password }),
  });

  if (!response.ok) {
    throw new Error('Failed to reset password');
  }
};

// Compatibility wrappers for components expecting older API names
export const getUsers = async (): Promise<AdminUser[]> => {
  const res = await fetchAdminUsers();
  return res.users;
};

export const deleteUser = async (userId: string): Promise<void> => {
  const response = await fetch(`${API_ROOT}/users/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to delete user');
  }
};

export const updateUserAccountStatus = async (
  userId: string,
  status: 'good' | 'past_due' | 'suspended',
): Promise<void> => {
  const response = await fetch(`${API_ROOT}/users/${userId}/account-status`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ accountStatus: status }),
  });

  if (!response.ok) {
    throw new Error('Failed to update account status');
  }
};

export type AdminPermissionKey =
  | 'payments'
  | 'campaignPackages'
  | 'adminGuide'
  | 'adAnalytics'
  | 'billboardTools';

export const updateUserAdminPermissions = async (
  userId: string,
  adminPermissions: AdminPermissionKey[],
): Promise<void> => {
  const response = await fetch(`${API_ROOT}/users/${userId}/admin-permissions`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ adminPermissions }),
  });

  if (!response.ok) {
    throw new Error('Failed to update admin permissions');
  }
};

export const fetchAdminStats = async (): Promise<AdminDashboardStats> => {
  const response = await fetch(`${API_ROOT}/stats`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch admin stats');
  }

  return response.json();
};

export interface SystemHealthSnapshot {
  status: 'ok' | 'degraded';
  mongo: {
    connected: boolean;
    state: number;
  };
  stripe: {
    configured: boolean;
    secretKeyPresent?: boolean;
    webhookSecretPresent?: boolean;
  };
  env: {
    nodeEnv: string;
    port: number | string;
  };
  uptimeSeconds: number;
  timestamp: string;
}

export const getSystemHealth = async (): Promise<SystemHealthSnapshot> => {
  const base = process.env.REACT_APP_API_BASE || 'http://localhost:5001';
  const response = await fetch(`${base}/health`, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch system health');
  }

  return response.json();
};
